import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BadgeCheck, Banknote, CheckCircle2, Download, Send, Users } from 'lucide-react';
import { PageHead } from '../components/bits.jsx';
import {
  Avatar,
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHead,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  KeyValue,
  Loading,
  Modal,
  Stat,
  Tabs,
  useToast,
} from '../components/ui.jsx';
import { api, downloadText } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { dateLabel, hours, money, num, titleCase } from '../lib/format.js';

export default function PayrollRun() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { data, loading, refresh } = useApi(`/payroll/runs/${id}`, { deps: [id] });
  const [tab, setTab] = useState('payouts');
  const [payOpen, setPayOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [adjust, setAdjust] = useState(null);
  const [busy, setBusy] = useState(false);

  if (loading && !data) {
    return (
      <>
        <PageHead title="Pay run" subtitle="Loading…" />
        <Loading rows={6} />
      </>
    );
  }
  if (!data?.run) {
    return (
      <Card>
        <EmptyState icon={Banknote} title="Pay run not found">
          <Link to="/app/payroll">Back to settlements</Link>
        </EmptyState>
      </Card>
    );
  }

  const { run, payouts, items } = data;
  const totalHours = payouts.reduce((sum, payout) => sum + Number(payout.hours || 0), 0);

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/payroll/runs/${id}/approve`);
      toast.ok('Run approved', 'Ready to release the funds');
      setApproveOpen(false);
      refresh();
    } catch (err) {
      toast.err('Could not approve the run', err.message);
    } finally {
      setBusy(false);
    }
  };

  const pay = async (reference) => {
    setBusy(true);
    try {
      const payload = await api.post(`/payroll/runs/${id}/pay`, { reference });
      toast.ok('Run paid', `${payload.data.jobsMarkedPaid} jobs marked paid · ${payload.data.reference}`);
      setPayOpen(false);
      refresh();
    } catch (err) {
      toast.err('Could not pay the run', err.message);
    } finally {
      setBusy(false);
    }
  };

  const saveAdjustment = async (payoutId, adjustments) => {
    setBusy(true);
    try {
      await api.patch(`/payroll/payouts/${payoutId}`, { adjustments: Number(adjustments) });
      toast.ok('Payout adjusted');
      setAdjust(null);
      refresh();
    } catch (err) {
      toast.err('Could not adjust the payout', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHead
        breadcrumb={[{ label: 'Paying lumpers', to: '/app/payroll' }, { label: run.code }]}
        title={run.code}
        subtitle={
          <span className="row gap-10 wrap">
            <Badge tone={run.status === 'paid' ? 'green' : run.status === 'approved' ? 'teal' : 'amber'}>{titleCase(run.status)}</Badge>
            <span className="dim">{dateLabel(run.periodStart)} → {dateLabel(run.periodEnd)}</span>
            <span className="dim">{run.lumpers} lumpers · {run.jobsCount} jobs</span>
            {run.approvedBy ? <span className="dim">approved by {run.approvedBy}</span> : null}
          </span>
        }
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              icon={Download}
              onClick={() => downloadText(`/payroll/runs/${id}/export`, `${run.code}-settlement.csv`).catch((err) => toast.err('Export failed', err.message))}
            >
              Export CSV
            </Button>
            {run.status === 'draft' ? (
              <Button variant="primary" size="sm" icon={BadgeCheck} loading={busy} onClick={() => setApproveOpen(true)}>Approve run</Button>
            ) : null}
            {run.status !== 'paid' ? (
              <Button variant="primary" size="sm" icon={Send} onClick={() => setPayOpen(true)}>Pay lumpers</Button>
            ) : (
              <Badge tone="green" size="lg" dot>Paid {run.payDate ? dateLabel(run.payDate) : ''}</Badge>
            )}
          </>
        }
      />

      <div className="grid grid-4 mb-20">
        <Stat label="Earned before charges" icon={Banknote} value={money(run.gross)} tone="amber" foot={`${num(totalHours.toFixed(1))} hours worked`} />
        <Stat label="Payout charges" icon={Banknote} value={money(run.fees)} tone="muted" foot="per-job payout cost" />
        <Stat label="Adjustments" icon={Banknote} value={money(run.adjustments)} tone={Number(run.adjustments) < 0 ? 'red' : 'green'} foot="bonuses and deductions" />
        <Stat label="What they get" icon={CheckCircle2} value={money(run.net)} tone="green" foot={`${run.lumpers} lumpers`} />
      </div>

      {run.status === 'draft' ? (
        <Callout tone="amber" icon={BadgeCheck} title="This run is still a draft" className="mb-20">
          Approve it once the numbers look right, then pay it. Lumpers see the settlement as soon as it is paid.
        </Callout>
      ) : null}

      {run.status === 'paid' ? (
        <Callout tone="green" icon={CheckCircle2} title={`Paid ${run.payDate ? dateLabel(run.payDate) : ''}`} className="mb-20">
          Every job in this run has been marked paid. {run.notes || ''}
        </Callout>
      ) : null}

      <Tabs
        className="mb-20"
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'payouts', label: 'Per lumper', icon: Users, count: payouts.length },
          { value: 'items', label: 'Line items', icon: Banknote, count: items.length },
        ]}
      />

      {tab === 'payouts' ? (
        <Card>
          <CardHead title="Payout breakdown" subtitle="Adjust a payout if a bonus or deduction applies" icon={Users} />
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Lumper</th>
                  <th className="narrow">Method</th>
                  <th className="right">Jobs</th>
                  <th className="right">Hours</th>
                  <th className="right">Earned</th>
                  <th className="right">Fees</th>
                  <th className="right">Adjust</th>
                  <th className="right">Net</th>
                  <th className="narrow">Status</th>
                  <th className="narrow right" />
                </tr>
              </thead>
              <tbody>
                {payouts.map((payout) => (
                  <tr key={payout.id}>
                    <td>
                      <div className="row gap-10">
                        <Avatar name={payout.lumperName} tone={payout.lumperPhotoColor || 'amber'} size="sm" />
                        <div className="col gap-2 truncate">
                          <Link to={`/app/lumpers/${payout.lumperId}`} className="truncate" style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>
                            {payout.lumperName}
                          </Link>
                          <span className="dimmer" style={{ fontSize: 10 }}>{payout.lumperCode} · {titleCase(payout.lumperTier)}</span>
                        </div>
                      </div>
                    </td>
                    <td className="narrow"><Badge tone="muted" size="sm">{payout.method}</Badge></td>
                    <td className="right num">{payout.jobsCount}</td>
                    <td className="right num">{Number(payout.hours).toFixed(1)}</td>
                    <td className="right num">{money(payout.gross)}</td>
                    <td className="right num">{money(payout.fees)}</td>
                    <td className="right num" style={{ color: Number(payout.adjustments) < 0 ? 'var(--red)' : Number(payout.adjustments) > 0 ? 'var(--green)' : undefined }}>
                      {Number(payout.adjustments) ? money(payout.adjustments) : '—'}
                    </td>
                    <td className="right num" style={{ fontWeight: 640 }}>{money(payout.net)}</td>
                    <td className="narrow">
                      <Badge tone={payout.status === 'paid' ? 'green' : 'amber'} size="sm">{payout.status}</Badge>
                    </td>
                    <td className="narrow right">
                      <Button size="sm" variant="ghost" onClick={() => setAdjust(payout)} disabled={run.status === 'paid'}>
                        Adjust
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card>
          <CardHead title="Line items" subtitle="Every job that makes up this run" icon={Banknote} />
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Lumper</th>
                  <th>Job</th>
                  <th className="narrow">Completed</th>
                  <th className="right">Hours</th>
                  <th className="right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className={item.jobId ? 'clickable' : undefined} onClick={() => item.jobId && navigate(`/app/jobs/${item.jobId}`)}>
                    <td className="truncate">{item.lumperName}</td>
                    <td>
                      <div className="col gap-2">
                        <span className="mono" style={{ fontSize: 'var(--fs-sm)' }}>{item.jobCode || item.description}</span>
                        <span className="dimmer" style={{ fontSize: 10 }}>{item.description}</span>
                      </div>
                    </td>
                    <td className="narrow dim">{item.completedAt ? dateLabel(item.completedAt) : '—'}</td>
                    <td className="right num">{Number(item.hours).toFixed(1)}</td>
                    <td className="right num">{money(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <AdjustModal payout={adjust} onClose={() => setAdjust(null)} onSave={saveAdjustment} busy={busy} />

      <ConfirmDialog
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        onConfirm={approve}
        title={`Approve ${run.code}?`}
        body={`This locks in ${money(run.net)} across ${run.lumpers} lumpers. You can still adjust individual payouts until the run is paid.`}
        confirmLabel="Approve run"
        loading={busy}
      />

      <Modal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        title="Pay this run"
        subtitle={`${money(run.net)} across ${run.lumpers} lumpers`}
        footer={<PayFooter busy={busy} onPay={pay} onCancel={() => setPayOpen(false)} />}
      >
        <Field label="Payment reference" hint="Appears on every lumper’s settlement record.">
          <Input id="pay-reference" placeholder="ACH-2026-1011" defaultValue={`ACH${Date.now().toString().slice(-6)}`} />
        </Field>
        <p className="muted mt-16" style={{ fontSize: 'var(--fs-sm)' }}>
          Marking this run paid moves {run.jobsCount} jobs to “paid”, so they stop showing as outstanding pay.
        </p>
      </Modal>
    </>
  );
}

function PayFooter({ busy, onPay, onCancel }) {
  return (
    <>
      <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      <Button
        variant="primary"
        icon={Send}
        loading={busy}
        onClick={() => onPay(document.getElementById('pay-reference')?.value || undefined)}
      >
        Mark as paid
      </Button>
    </>
  );
}

function AdjustModal({ payout, onClose, onSave, busy }) {
  const [value, setValue] = useState('');
  if (!payout) return null;
  const adjustments = value === '' ? Number(payout.adjustments || 0) : Number(value);
  const net = Number(payout.gross) - Number(payout.fees) + adjustments;

  return (
    <Modal
      open
      onClose={onClose}
      title="Adjust this payout"
      subtitle={`${payout.lumperName} · ${payout.jobsCount} jobs`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} onClick={() => onSave(payout.id, adjustments)}>Save adjustment</Button>
        </>
      }
    >
      <Field label="Adjustment" hint="Positive adds a bonus, negative deducts (advances, damage, fees).">
        <Input className="mono" type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder={String(payout.adjustments || 0)} />
      </Field>
      <div className="divider" />
      <KeyValue label="Earned">{money(payout.gross)}</KeyValue>
      <KeyValue label="Fees">{money(payout.fees)}</KeyValue>
      <KeyValue label="Adjustment">{money(adjustments)}</KeyValue>
      <div className="divider" />
      <KeyValue label="New net"><span style={{ fontWeight: 660, fontSize: 'var(--fs-lg)' }}>{money(net)}</span></KeyValue>
    </Modal>
  );
}
